/**
 * Part Number Constants for V5000 System Components
 * Single source of truth for all hardware and software part numbers
 */

export const PART_NUMBERS = {
  // Node Hardware
  NODES: {
    DIRECTOR: 'VCH-5100-D1N',
    DIRECTOR_OLD: 'VCH-5000-D1N', // MORK TODO : To be deprecated
    HYBRID_STORAGE: 'VCH-5000-S1N',
    AFA_STORAGE: 'VCH-5000-F1N',
    AFA_STORAGE_DUAL_CPU: 'VCH-5500-F1N', // CONF-210: Dual CPU flash node (dualCpuAccess)
    HYBRID_DUAL_ACTUATOR: 'VCH-5050-S1N' // MORK TODO : to be deprecated
  },

  // Storage Components
  STORAGE: {
    SSD_BASE: 'VCH-NVME-',  // Capacity gets appended
    HDD_BASE: 'VCH-5000-J'  // Size and capacity get appended
  },

  // Network Interface Cards

  NETWORK: {
    ETHERNET: {
      // ============================================================================
      // 12/8/2025 TEMPORARY OVERRIDE - CONF-45
      // ============================================================================
      // MORK TODO : Director nodes are temporarily using VCH-5000-NDR200-2P-OCP for ALL network
      // types (both Ethernet and InfiniBand) until director code functionality is
      // implemented. Normally this would vary based on network type.
      // 
      // TO REVERT: Change this back to the appropriate Ethernet-specific part number
      // once the director code changes are complete. 
      //
      //        DIRECTOR_OCP: 'VCH-5000-200GbE-2P-OCP'
      //
      // ============================================================================
      DIRECTOR_OCP: 'VCH-5000-NDR200-2P-OCP',
      STORAGE_PCIE: 'VCH-5000-200GBE-2P-PCIe',
      STORAGE_OCP: 'VCH-5000-200GBE-2P-OCP'
    },
    INFINIBAND: {
      DIRECTOR_OCP: 'VCH-5000-NDR200-2P-OCP',
      STORAGE_PCIE: 'VCH-5000-NDR200-2P-PCIe',
      STORAGE_OCP: 'VCH-5000-NDR200-2P-OCP'
    }
  },

  // Software Components
  // VDURA_CARE is the complete bundle of both the Software Subscription/License and the Hardware Support
  SOFTWARE: {
    VDURA_CARE: {
      SSD_STANDARD: 'VDP-VDURACare-10-HP',
      SSD_NO_RETURN_MEDIA: 'VDP-VDURACare-10-HP-NR-Media',
      SSD_NO_RETURN_HARDWARE: 'VDP-VDURACare-10-HP-NR-Server',
      HDD_STANDARD: 'VDP-VDURACare-10-C',
      HDD_NO_RETURN_MEDIA: 'VDP-VDURACare-10-C-NR-Media',
      HDD_NO_RETURN_HARDWARE: 'VDP-VDURACare-10-C-NR-Server'
    },
    // VDURACare License sub-component
    VDURA_CARE_LICENSE: {
      SSD_STANDARD: 'VDP-SW-P-10-HP',
      SSD_NO_RETURN_MEDIA: 'VDP-SW-P-10-HP',
      SSD_NO_RETURN_HARDWARE: 'VDP-SW-P-10-HP',
      HDD_STANDARD: 'VDP-SW-P-10-C',
      HDD_NO_RETURN_MEDIA: 'VDP-SW-P-10-C',
      HDD_NO_RETURN_HARDWARE: 'VDP-SW-P-10-C'
    },
    // VDURACare Hardware Support sub-component
    VDURA_CARE_HW_SUPPORT: {
      SSD_STANDARD: 'HW-Support-HP-NBD',
      SSD_NO_RETURN_MEDIA: 'HW-Support-HP-NBD-NR-Media',
      SSD_NO_RETURN_HARDWARE: 'HW-Support-HP-NBD-NR-NM',
      HDD_STANDARD: 'HW-Support-C-NBD',
      HDD_NO_RETURN_MEDIA: 'HW-Support-C-NBD-NR-Media',
      HDD_NO_RETURN_HARDWARE: 'HW-Support-C-NBD-NR-NM'
    },
    DISCOUNT: 'VDP-SW-P-10-PD'
  },

  // Services
  SERVICES: {
    INSTALLATION: 'SVC-R1-CINT-PDEP-NORACK'
  }
};

// System component categories for easy iteration
export const COMPONENT_CATEGORIES = {
  HARDWARE: ['NODES', 'STORAGE', 'NETWORK'],
  SOFTWARE: ['SOFTWARE'],
  SERVICES: ['SERVICES']
};

/**
 * CONF-210. The flash (F-node) models the configurator can sell, keyed by the
 * flat-config value `afaNodeModel` (absent means `standard`).
 *
 * This table is the ONE place that says which SKU a flash node is and how many
 * network cards it takes. Part numbers, NIC counts, pricing, BOM rows, network
 * ports, metrics power/weight and the attribute-card / rack-slide labels all
 * read it through resolveFlashNodeModel (src/utils/flashNodeModel.js), so the
 * BOM and the metrics cannot disagree the way HYBRID_DUAL_ACTUATOR does
 * (chosen only in hybridNodeMetrics, never in the BOM).
 *
 * `nicLayout` is cards per node. Both models use the selected network option's
 * card SKUs; only the counts differ.
 */
export const FLASH_NODE_MODELS = {
  standard: {
    id: 'standard',
    partNumber: PART_NUMBERS.NODES.AFA_STORAGE,
    nicLayout: { pcie: 1, ocp: 1 },
    label: 'F-Nodes',
    hardwareRowLabel: 'Flash Nodes (F1N)',
  },
  dualCpu: {
    id: 'dualCpu',
    partNumber: PART_NUMBERS.NODES.AFA_STORAGE_DUAL_CPU,
    nicLayout: { pcie: 2, ocp: 1 },
    label: 'Dual CPU F-Nodes',
    hardwareRowLabel: 'Dual CPU Flash Nodes (F1N)',
  },
};

// Node types for configuration logic
export const NODE_TYPES = {
  DIRECTOR: 'director',
  HYBRID_STORAGE: 'hybrid',
  AFA_STORAGE: 'afa',
  HYBRID_DUAL_ACTUATOR: 'hybridDualActuator'
};

export const DRIVE_TYPES = {
  SSD_TLC: 'tlc',
  SSD_QLC: 'qlc',
  HDD: 'hdd'
}